import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, Battery, Shield, Zap, Cloud, Smartphone, Globe } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Button } from '../../components/ui/button';

const TeslaPowerwallPage = () => {
  const features = [
    {
      icon: Shield,
      title: "Backup Protection",
      description: "Powerwall detects outages and automatically becomes your home's energy source when the grid goes down."
    },
    {
      icon: Cloud,
      title: "Storm Watch",
      description: "When severe weather is forecast, Powerwall automatically charges to maximum capacity to prepare your home."
    },
    {
      icon: Zap,
      title: "Time-Based Control",
      description: "Use stored energy during peak rate periods to maximize savings on your electricity bill."
    },
    {
      icon: Smartphone,
      title: "Tesla App",
      description: "Monitor and control your Powerwall from anywhere with real-time energy data and insights."
    }
  ];

  const specs = [
    { label: "Energy Capacity", value: "13.5 kWh" },
    { label: "On-Grid Power", value: "5 kW continuous" },
    { label: "Backup Power", value: "7 kW peak" },
    { label: "Round Trip Efficiency", value: "90%" },
    { label: "Warranty", value: "10 years" },
    { label: "Installation", value: "Indoor/Outdoor" }
  ];

  const benefits = [
    "Store solar energy for use anytime",
    "Protect your home during outages",
    "Reduce reliance on the grid",
    "Export power when prices spike",
    "Seamless integration with solar",
    "Sleek, compact design"
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#3D5A47]/5 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Breadcrumb */}
            <Link to="/brands" className="inline-flex items-center gap-2 text-[#3D5A47] hover:text-[#B5D43B] mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Trusted Brands</span>
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Battery className="w-5 h-5 text-[#B5D43B]" />
                  <span className="text-sm font-semibold text-[#B5D43B]">Battery Storage</span>
                </div>
                <h1 className="heading-1 mb-4">
                  <span className="text-[#3D5A47]">Tesla</span>{' '}
                  <span className="text-[#B5D43B]">Powerwall</span>
                </h1>
                <p className="text-xl text-[#3D5A47] font-medium mb-4">
                  Security. Independence. Control.
                </p>
                <p className="body-large text-gray-600 mb-8">
                  Tesla Powerwall is a solar battery that stores energy, detects outages and automatically becomes your home's energy source when the grid goes down. Experience true energy independence with the world's most trusted home battery.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact">
                    <Button className="btn-primary gap-2">
                      Get a Tesla Powerwall Quote
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="https://www.tesla.com/en_au/powerwall" target="_blank" rel="noopener noreferrer">
                    <Button className="btn-secondary gap-2">
                      Visit Tesla Website
                      <Globe className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
              <div className="flex justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_greenenergy-site/artifacts/r24ticpt_image.png"
                  alt="Tesla Powerwall"
                  className="w-full max-w-md rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-12">Powerwall Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-2xl hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-semibold text-[#3D5A47] mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Specs & Benefits */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Specifications */}
              <div>
                <h2 className="heading-2 mb-6">Technical Specifications</h2>
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  {specs.map((spec, index) => (
                    <div 
                      key={index} 
                      className={`flex justify-between items-center p-4 ${index !== specs.length - 1 ? 'border-b border-gray-100' : ''}`}
                    >
                      <span className="text-gray-600">{spec.label}</span>
                      <span className="font-semibold text-[#3D5A47]">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div>
                <h2 className="heading-2 mb-6">Key Benefits</h2>
                <div className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
                      <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0" />
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-4">How Powerwall Works</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              Powerwall integrates with solar to store excess energy generated during the day and makes it available when you need it.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#B5D43B]/15 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#3D5A47]">1</span>
                </div>
                <h3 className="font-semibold text-[#3D5A47] mb-2">Store Solar Energy</h3>
                <p className="text-gray-600 text-sm">
                  During the day, your solar panels generate more energy than your home uses. Powerwall stores this excess.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#B5D43B]/15 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#3D5A47]">2</span>
                </div>
                <h3 className="font-semibold text-[#3D5A47] mb-2">Power Your Home</h3>
                <p className="text-gray-600 text-sm">
                  In the evening or during peak times, Powerwall releases stored energy to power your home and reduce grid reliance.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-[#B5D43B]/15 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-[#3D5A47]">3</span>
                </div>
                <h3 className="font-semibold text-[#3D5A47] mb-2">Backup Protection</h3>
                <p className="text-gray-600 text-sm">
                  When the grid goes down, Powerwall automatically kicks in to keep your lights on and essentials running.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#3D5A47]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="heading-2 text-white mb-4">Ready for Energy Independence?</h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              As a Tesla Powerwall Certified Installer, we can help you achieve true energy independence with the world's most trusted home battery.
            </p>
            <Link to="/contact">
              <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2">
                Get Your Free Quote
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TeslaPowerwallPage;
